/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
import React, { useRef, useLayoutEffect, useState } from 'react';

import { evalLegacyConnectInlineScripts } from '@confluence/connect-utils';
import { expVal } from '@confluence/feature-experiments';
import { ActivatableComponent } from '@confluence/loadable/entry-points/ActivatableComponent';
import { ActivationTriggerType } from '@confluence/loadable';

type WebPanelProps = {
	html: string;
	/**
	 * Unique identifier for this web panel instance
	 */
	panelId?: string;
};

export function WebPanel({ html, panelId }: WebPanelProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [ssrSuccess, setSsrSuccess] = useState(true);
	const moduleLocationMatch = html.match(/"moduleLocation"\s*:\s*"atl\.general"/);

	// Restrict to just atl-general for now
	const isAtlGeneralWebpanel = Boolean(moduleLocationMatch);

	useLayoutEffect(() => {
		const container = containerRef.current;

		if (!container) {
			return;
		}

		if (
			isAtlGeneralWebpanel &&
			expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false)
		) {
			if (window?.__HYDRATABLE__) {
				const scriptElement = container.querySelector('script.ap-iframe-body-script');
				if (scriptElement) {
					const scriptContent = scriptElement.textContent || '';
					const match = scriptContent.match(/"uniqueKey":"([^"]+)"/);
					if (match) {
						const uniqueKey = match[1];
						// Find iframe whose id contains the uniqueKey
						const iframe = container.querySelector(`iframe[id*="${uniqueKey}"]`);
						if (iframe) {
							return;
						}
					}
				}
			}

			if (ssrSuccess) {
				setSsrSuccess(false);
			}
		}
		void evalLegacyConnectInlineScripts(
			container,
			expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false),
		);
	}, [html, panelId, ssrSuccess, isAtlGeneralWebpanel]);

	return isAtlGeneralWebpanel &&
		expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false) ? (
		<div ref={containerRef}>
			<ActivatableComponent
				id={panelId ?? 'WebPanel'}
				triggers={[{ type: ActivationTriggerType.SSR_ONLY }]}
				ssrSuccess={ssrSuccess}
				analyticsName="WebPanel"
			>
				<div data-vc-ignore-if-no-layout-shift dangerouslySetInnerHTML={{ __html: html }} />
			</ActivatableComponent>
		</div>
	) : (
		<div
			ref={containerRef}
			data-vc-ignore-if-no-layout-shift
			dangerouslySetInnerHTML={{ __html: html }}
		/>
	);
}
